<template>
  <v-dialog v-model="dialog" max-width="520" persistent>
    <v-card class="subscription-gate">
      <v-card-title class="headline error white--text">
        {{ isNativeMobileApp ? "Subscription inactive" : "Your subscription expired" }}
      </v-card-title>

      <v-card-text class="pt-6">
        <template v-if="isNativeMobileApp">
          <p class="body-1 mb-0">
            This organization does not currently have access. Contact your
            organization administrator to reactivate the service.
          </p>
          <v-alert v-if="errorMessage" type="error" dense class="mt-4">
            {{ errorMessage }}
          </v-alert>
        </template>

        <template v-else-if="!showPayment">
          <p class="body-1">
            Renew your subscription to keep using employee hours, reports, pay,
            and the admin tools. Employees can still clock in and clock out.
          </p>
          <p class="body-2 grey--text text--darken-1">
            We will restart the most recent plan on this Stripe customer. If
            there is no previous plan, we will start the $200 annual plan.
          </p>
          <v-alert v-if="errorMessage" type="error" dense>
            {{ errorMessage }}
          </v-alert>
        </template>

        <template v-else>
          <p class="body-1 mb-0">
            Enter a card to restart your subscription.
          </p>
          <CollectCard ref="card" />
          <v-alert v-if="errorMessage" type="error" dense class="mt-4">
            {{ errorMessage }}
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-btn v-if="isNativeMobileApp" text block to="/logout">
          Sign out
        </v-btn>
        <v-btn v-else-if="showPayment" text :disabled="renewing" @click="showPayment = false">
          Back
        </v-btn>
        <v-btn
          v-else-if="deferralsUsed < maxDeferrals"
          text
          :disabled="checking"
          @click="remindLater"
        >
          Later ({{ maxDeferrals - deferralsUsed }} left)
        </v-btn>
        <v-spacer v-if="!isNativeMobileApp" />
        <v-btn
          v-if="!isNativeMobileApp && !showPayment"
          color="primary"
          :disabled="checking"
          @click="openPayment"
        >
          Renew subscription
        </v-btn>
        <v-btn
          v-else-if="!isNativeMobileApp"
          color="primary"
          :loading="renewing"
          @click="renew"
        >
          Pay and renew
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CollectCard from "@/components/CollectCard.vue";
import locationService from "@/modules/Location/service.js";
import { isNativeMobileApp } from "@/utils/mobileApp";

const CHECK_INTERVAL = 2 * 60 * 60 * 1000;

export default {
  name: "subscription-gate",
  components: { CollectCard },
  data() {
    return {
      dialog: false,
      showPayment: false,
      checking: false,
      renewing: false,
      deferralsUsed: 0,
      maxDeferrals: 5,
      errorMessage: "",
      polling: null,
      locationId: null,
    };
  },
  created() {
    const location = JSON.parse(
      localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}location`)
    );
    if (!location) return;

    this.locationId = location.id;
    this.deferralsUsed = parseInt(localStorage.getItem(this.deferralKey), 10) || 0;
    this.loadStatus();
    this.polling = setInterval(() => this.loadStatus(true), CHECK_INTERVAL);
  },
  beforeDestroy() {
    if (this.polling) clearInterval(this.polling);
  },
  computed: {
    isNativeMobileApp() {
      return isNativeMobileApp();
    },
    deferralKey() {
      return `${process.env.VUE_APP_STORAGE_PREFIX}subscriptionDeferrals:${this.locationId}`;
    },
    statusKey() {
      return `${process.env.VUE_APP_STORAGE_PREFIX}subscriptionStatus:v2:${this.locationId}`;
    },
  },
  methods: {
    applyStatus(status) {
      if (status.hasAccess) {
        this.dialog = false;
        this.showPayment = false;
        this.errorMessage = "";
        this.deferralsUsed = 0;
        localStorage.removeItem(this.deferralKey);
      } else {
        this.dialog = true;
      }
    },
    async loadStatus(force = false) {
      if (!this.locationId || this.checking) return;
      const cached = JSON.parse(localStorage.getItem(this.statusKey) || "null");
      if (!force && cached && Date.now() - cached.checkedAt < CHECK_INTERVAL) {
        this.applyStatus(cached.status);
        return;
      }

      this.checking = true;
      try {
        const response = await locationService.getSubscriptionStatus();
        const status = response.data;
        localStorage.setItem(this.statusKey, JSON.stringify({ checkedAt: Date.now(), status }));
        this.applyStatus(status);
      } catch (error) {
        // Do not lock paying customers out during a temporary API/Stripe outage.
        this.errorMessage = "We could not verify the subscription. We will try again later.";
      } finally {
        this.checking = false;
      }
    },
    remindLater() {
      if (this.deferralsUsed >= this.maxDeferrals) return;
      this.deferralsUsed += 1;
      localStorage.setItem(this.deferralKey, this.deferralsUsed);
      this.dialog = false;
    },
    openPayment() {
      this.errorMessage = "";
      this.showPayment = true;
    },
    async renew() {
      this.errorMessage = "";
      this.renewing = true;
      try {
        const token = await this.$refs.card.getCard();
        if (!token || token.error || !token.token) {
          this.errorMessage = token && token.error
            ? token.error.message
            : "Please enter a valid card.";
          return;
        }
        const response = await locationService.renewSubscription({ source: token.token.id });
        if (!response.data.hasAccess) {
          this.errorMessage = "Stripe could not complete the payment. Please try another card.";
          return;
        }
        localStorage.removeItem(this.statusKey);
        this.applyStatus(response.data);
        this.$toast.success("Your subscription is active.", { timeout: 4000 });
      } catch (error) {
        this.errorMessage = error.data && error.data.message
          ? error.data.message
          : "We could not renew the subscription. Please try again.";
      } finally {
        this.renewing = false;
      }
    },
  },
};
</script>

<style scoped>
.subscription-gate {
  overflow: hidden;
}
</style>
